import classNames from 'classnames';

import {Avatar} from 'shells/avatar/avatar';
import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {WarningIcon} from 'shells/icon/svg/warning-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import type {
    UserQuotaFragment as User,
    UserQuotaFragment_quotas as Quota,
} from 'nutshell-graphql-types';

import {QuotaHeaderOverview} from './quota-header-overview';

import styles from './user-quota-card-header.module.css';

type Props = {
    user: User;
    fiscalYear: number;
    previousYearQuota: Quota | null | undefined;
    existingQuota: Quota | null | undefined;
    isEditing: boolean;
    onEdit?: () => void;
    onCopyFromPreviousYear?: () => void;
    showStatusBadge: boolean;
    isOpen: boolean;
    hasActionMenu: boolean;
    formattedYearTotal: string;
};

export function UserQuotaCardHeader(props: Props) {
    const statAndButtonSectionStyles = classNames(
        styles['stat-and-button-section'],
        // without action menu, we need right padding to align properly
        !props.hasActionMenu && styles['stat-and-button-section--no-action-menu']
    );

    return (
        <Stack isHorizontal={true} justifyContent='space-between' isFullWidth={true}>
            <Stack isHorizontal={true} spacing={12} alignItems='center'>
                <Avatar
                    type='users'
                    avatarUrl={props.user.avatarUrl}
                    initials={props.user.initials}
                    size={32}
                />

                <Stack>
                    <Body color='dark' isBold={true}>
                        {props.user.name}
                    </Body>
                    {props.showStatusBadge && (
                        <Stack isHorizontal={true} alignItems='center' spacing={4}>
                            {/* @ts-expect-error */}
                            <WarningIcon size={13} alignCenter={true} fill={colors.yellow} />
                            <Body size='small'>No quota set for {props.fiscalYear}</Body>
                        </Stack>
                    )}
                </Stack>
            </Stack>
            <div className={statAndButtonSectionStyles}>
                {!props.isOpen && props.existingQuota && (
                    <QuotaHeaderOverview
                        user={props.user}
                        existingQuota={props.existingQuota}
                        formattedYearTotal={props.formattedYearTotal}
                    />
                )}
                {props.previousYearQuota &&
                    !props.isEditing &&
                    !props.existingQuota &&
                    props.onCopyFromPreviousYear && (
                        <Button
                            type='button'
                            onClick={(e) => {
                                e.stopPropagation();
                                props.onCopyFromPreviousYear?.();
                            }}
                        >
                            Copy from {props.previousYearQuota.fiscalYear} quota
                        </Button>
                    )}
                {props.onEdit && !props.isEditing && (
                    <Button
                        type='button'
                        variant='text-primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onEdit?.();
                        }}
                    >
                        Edit
                    </Button>
                )}
            </div>
        </Stack>
    );
}
